import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, comics, comicTags } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTag, setActiveTag] = useState<string>('全部');
  const [sortBy, setSortBy] = useState<'rating' | 'views' | 'updated'>('rating');

  const currentCat = categories.find((c) => c.id === slug);
  const catName = currentCat?.name || '全部分类';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    setActiveTag('全部');
  }, [slug]);

  let filtered = comics.filter((c) => c.category === slug);

  if (activeTag !== '全部') {
    if (activeTag === '独家') filtered = filtered.filter((c) => c.tags.includes('独家'));
    if (activeTag === '热门') filtered = filtered.filter((c) => c.tags.includes('热门'));
    if (activeTag === '新作') filtered = filtered.filter((c) => c.tags.includes('新作'));
    if (activeTag === '完结') filtered = filtered.filter((c) => c.status === '完结');
    if (activeTag === '连载中') filtered = filtered.filter((c) => c.status === '连载中');
  }

  filtered = [...filtered].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'views') return parseFloat(b.views) - parseFloat(a.views);
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  const sortOptions: { key: 'rating' | 'views' | 'updated'; label: string }[] = [
    { key: 'rating', label: '评分最高' },
    { key: 'views', label: '人气最高' },
    { key: 'updated', label: '最近更新' },
  ];

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: catName },
  ];

  const pageTitle = `${catName} - 韩漫之家 - 免费漫画在线阅读`;
  const pageDesc = `韩漫之家${catName}分类，海量正版韩国漫画免费在线阅读。精选${catName}类漫画，每日同步更新。`;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {catName}
            </h1>
            <p className="text-sm text-foreground-500">
              共 {filtered.length} 部作品 · 韩漫之家正版收录
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center flex-wrap gap-2">
              <button
                onClick={() => setActiveTag('全部')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                  activeTag === '全部'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {comicTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setActiveTag(tag)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    activeTag === tag
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-background-100 text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  cat.id === slug
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <span className={`w-5 h-5 flex items-center justify-center rounded ${cat.color}`}>
                  <i className={`${cat.icon} text-[10px]`}></i>
                </span>
                {cat.name}
              </Link>
            ))}
          </div>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5 pb-16" data-product-shop>
              {filtered.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                    <img
                      src={comic.cover}
                      alt={`${comic.title} - ${catName} 韩国漫画免费在线`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                      {comic.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 text-[10px] font-medium bg-primary-500/90 text-background-50 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-amber-400 text-xs"></i>
                        <span className="text-xs text-background-50 font-medium">{comic.rating}</span>
                        <span className="text-xs text-background-200 ml-auto">{comic.views}</span>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{comic.author} · {comic.status}</p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-24">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无漫画</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}